import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { useCart } from "../../lib/useCart";
import { ChevronLeft, ChevronRight, Plus, Flame, Tag, Sparkles, Package } from "lucide-react";
import { toast } from "sonner";
import WishlistButton from "./WishlistButton";

function priceFmt(cents, symbol = "€") {
  return `${(cents / 100).toFixed(2)} ${symbol}`;
}

/**
 * Shared horizontal product carousel used by "popular", "promotions" and "new" homepage sections.
 * Variant controls copy, icon, accent color and product filtering strategy.
 */
export default function ShopProductsRow({ variant, testId }) {
  const { settings: s } = useSettings();
  const { add } = useCart();
  const currency = s?.shop_currency_symbol || "€";
  const contentKey = {
    popular: "shop_popular",
    promotions: "shop_promotions",
    new: "shop_new",
  }[variant];
  const c = s?.home_sections_content?.[contentKey] || {};
  const [items, setItems] = useState([]);
  const scroller = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        let products = [];
        if (variant === "popular") {
          const r = await api.get("/shop/products", { params: { featured: true } });
          products = r.data || [];
          if (products.length < 4) {
            // fallback: any published
            const all = await api.get("/shop/products");
            const seen = new Set(products.map(p => p.id));
            for (const p of all.data || []) {
              if (!seen.has(p.id)) products.push(p);
            }
          }
        } else if (variant === "promotions") {
          const r = await api.get("/shop/products");
          products = (r.data || []).filter(p => (p.compare_at_price_cents || 0) > (p.price_cents || 0));
        } else if (variant === "new") {
          const r = await api.get("/shop/products");
          products = [...(r.data || [])].sort((a, b) => {
            const ta = a.created_at || "";
            const tb = b.created_at || "";
            return tb.localeCompare(ta);
          });
        }
        const limit = Number(c.limit) > 0 ? Number(c.limit) : 8;
        setItems(products.slice(0, limit));
      } catch {
        setItems([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, c.limit]);

  if (!items.length) return null;

  const VariantIcon = variant === "popular" ? Flame : variant === "promotions" ? Tag : Sparkles;
  const accent = variant === "promotions" ? "var(--brand-red)" : "var(--brand-sky)";

  const scroll = (dir) => {
    if (!scroller.current) return;
    scroller.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  const quickAdd = (p) => {
    if (p.stock_tracking && (p.stock_qty || 0) <= 0) {
      toast.error("Produit en rupture");
      return;
    }
    add({ id: p.id, name: p.name, price_cents: p.price_cents, image: p.featured_image, slug: p.slug }, 1);
    toast.success(`${p.name} ajouté au panier`);
  };

  return (
    <section className="py-16 md:py-20 bg-[hsl(var(--background))]" data-testid={testId}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div className="flex gap-4">
            {c.image ? (
              <img src={c.image} alt="" className="w-14 h-14 rounded-xl object-cover shrink-0 hidden md:block" />
            ) : (
              <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0 hidden md:flex" style={{ background: `color-mix(in srgb, ${accent} 15%, transparent)` }}>
                <VariantIcon className="w-6 h-6" style={{ color: `color-mix(in srgb, ${accent} 90%, #000)` }} />
              </div>
            )}
            <div>
              <div className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold mb-2" style={{ color: `color-mix(in srgb, ${accent} 90%, #000)` }}>
                {c.kicker || (variant === "popular" ? "Best-sellers" : variant === "promotions" ? "Offres du moment" : "Nouveautés")}
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[color:var(--brand-navy)]">
                {c.title || (variant === "popular" ? "Produits les plus populaires" : variant === "promotions" ? "Profitez-en avant la fin !" : "Tout juste arrivés")}
              </h2>
              {c.subtitle && <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-xl">{c.subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/shop" className="text-xs font-semibold text-[color:var(--brand-navy)] hover:text-[color:var(--brand-sky)] mr-2 hidden md:inline">
              Voir la boutique →
            </Link>
            <button type="button" onClick={() => scroll(-1)} className="w-9 h-9 rounded-full border border-[hsl(var(--border))] hover:bg-white flex items-center justify-center bg-white" aria-label="Précédent">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => scroll(1)} className="w-9 h-9 rounded-full border border-[hsl(var(--border))] hover:bg-white flex items-center justify-center bg-white" aria-label="Suivant">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div ref={scroller} className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth no-scrollbar">
          {items.map((p) => {
            const hasDiscount = (p.compare_at_price_cents || 0) > (p.price_cents || 0);
            const discountPct = hasDiscount
              ? Math.round(((p.compare_at_price_cents - p.price_cents) / p.compare_at_price_cents) * 100)
              : 0;
            return (
              <div
                key={p.id}
                className="group relative flex-shrink-0 w-[220px] md:w-[260px] snap-start rounded-2xl overflow-hidden border border-[hsl(var(--border))] bg-white hover:shadow-xl transition-all flex flex-col"
                data-testid={`${testId}-item-${p.slug}`}
              >
                <Link to={`/shop/${p.slug}`} className="block relative">
                  <div className="aspect-square bg-[hsl(var(--muted))]/30 relative overflow-hidden">
                    {p.featured_image ? (
                      <img src={p.featured_image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-12 h-12 text-[color:var(--brand-navy)]/20" />
                      </div>
                    )}
                    {hasDiscount && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-black tracking-wider uppercase rounded-full bg-[color:var(--brand-red)] text-white">
                        -{discountPct}%
                      </span>
                    )}
                    {variant === "new" && !hasDiscount && (
                      <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-black tracking-wider uppercase rounded-full bg-[color:var(--brand-navy)] text-white">
                        Nouveau
                      </span>
                    )}
                    <div className="absolute top-2 right-2">
                      <WishlistButton productId={p.id} variant="icon" />
                    </div>
                  </div>
                </Link>
                <div className="p-3 flex-1 flex flex-col">
                  <Link to={`/shop/${p.slug}`} className="font-semibold text-sm text-[color:var(--brand-navy)] line-clamp-2 hover:text-[color:var(--brand-sky)] flex-1">
                    {p.name}
                  </Link>
                  <div className="flex items-end justify-between mt-2 gap-2">
                    <div>
                      <div className="flex items-baseline gap-1.5">
                        <span className={`text-base font-bold ${hasDiscount ? "text-[color:var(--brand-red)]" : "text-[color:var(--brand-navy)]"}`}>
                          {priceFmt(p.price_cents, currency)}
                        </span>
                        {hasDiscount && (
                          <span className="text-[11px] line-through text-[hsl(var(--muted-foreground))]">
                            {priceFmt(p.compare_at_price_cents, currency)}
                          </span>
                        )}
                      </div>
                      {/* Réglementation alimentaire FR/UE — prix au kilo obligatoire */}
                      {!p.price_per_kg && p.is_food && p.unit_weight_g > 0 && p.price_cents > 0 && (
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5" data-testid={`${testId}-unit-price-${p.slug}`}>
                          {priceFmt(Math.round(p.price_cents / p.unit_weight_g * 1000), currency)} / kg · {p.unit_weight_g} g
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => quickAdd(p)}
                      className="w-8 h-8 rounded-full bg-[color:var(--brand-navy)] text-white hover:bg-[color:var(--brand-sky)] flex items-center justify-center shrink-0"
                      aria-label="Ajouter au panier"
                      data-testid={`${testId}-quickadd-${p.slug}`}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
